"use client";

import { useServerErrorMessage } from "@/lib/i18n/use-server-error-message";
import { fetchTurnstileToken } from "@/lib/turnstile-client";
import {
  isVisitorVerificationRequired,
  prefetchTurnstileToken,
  requestVisitorGrant,
  type TurnstilePrefetch,
  type VisitorGrantTarget,
  visitorGrantFailureKey,
} from "@/lib/visitor-chat-grant";
import type { UIMessage } from "@convex-dev/agent";
import { useUIMessages } from "@convex-dev/agent/react";
import { api } from "@v1/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { rememberVisitorChatToken, visitorChatScope } from "../customer-chat/customer-chat-session-storage";
import { MessengerAgentAvatar } from "./messenger-avatars";
import { type MessengerBubbleItem, MessengerBubbles } from "./messenger-bubbles";
import { MessengerComposer } from "./messenger-composer";
import { useMessenger } from "./messenger-context";
import { MessengerDetailHeader, MessengerNewChatButton } from "./messenger-header";
import { MessengerFailedRow, MessengerLoadOlder, MessengerTypingBubble } from "./messenger-thread-parts";
import { isAwaitingAgentAnswer, isFullUIMessage, toMessengerBubble } from "./messenger-ui-message";
import { useMessengerPendingSend } from "./use-messenger-pending-send";
import { useMessengerStickToBottom } from "./use-messenger-scroll";

const PAGE_SIZE = 30;
const LIST_OPTIONS = { initialNumItems: PAGE_SIZE, stream: true } as const;

interface VisitorSession {
  readonly threadId: string;
  readonly token: string;
}

/** What differs between help center and website: session calls, token scope, bot-check target. */
interface VisitorChannelApi {
  /** Read at grant time: the website's handshake token is renewed while the chat stays open. */
  readonly grantTarget: () => VisitorGrantTarget;
  /** Where this browser keeps the visitor's chat tokens. */
  readonly scope: string;
  readonly restore: (token: string) => Promise<VisitorSession>;
  readonly send: (session: VisitorSession, text: string) => Promise<unknown>;
}

interface VisitorMessages {
  readonly rows: readonly UIMessage[];
  readonly status: string;
  readonly loadMore: (count: number) => void;
}

/** An opened chat loads by its token; a new chat (null) has none yet. */
function useRestoredSession(
  channel: VisitorChannelApi,
  token: string | null,
): [VisitorSession | null, (next: VisitorSession) => void] {
  const [session, setSession] = useState<VisitorSession | null>(null);
  useEffect(() => {
    if (token === null) return;
    let cancelled = false;
    void channel
      .restore(token)
      .then((restored) => {
        if (!cancelled) setSession(restored);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [channel, token]);
  return [session, setSession];
}

/**
 * Anonymous visitor's chat; each chat has its own token, kept in this browser.
 * `token` null = new chat. `askText` is sent once on open.
 */
export function MessengerVisitorChatView({
  token,
  askText,
}: {
  readonly token: string | null;
  readonly askText: string | null;
}): ReactElement {
  const { audience } = useMessenger();
  if (audience.kind !== "visitor") throw new Error("visitor chat rendered for a member");
  const { channel } = audience;
  return channel.kind === "website" ? (
    <WebsiteVisitorChat token={token} askText={askText} siteKey={channel.siteKey} handshakeToken={channel.handshakeToken} />
  ) : (
    <HelpCenterVisitorChat token={token} askText={askText} orgSlug={channel.orgSlug} />
  );
}

function HelpCenterVisitorChat({
  token,
  askText,
  orgSlug,
}: {
  readonly token: string | null;
  readonly askText: string | null;
  readonly orgSlug: string;
}): ReactElement {
  const initSession = useMutation(api.customerChat.public_widget.initSession);
  const sendMessage = useMutation(api.customerChat.public_messages.sendMessage);
  const channel = useMemo(
    (): VisitorChannelApi => ({
      grantTarget: () => ({ surface: "help_center", orgSlug }),
      scope: visitorChatScope({ kind: "help_center", orgSlug }),
      restore: async (visitorToken) => {
        const result = await initSession({ org_slug: orgSlug, visitor_token: visitorToken });
        return { threadId: result.thread_id, token: result.visitor_token };
      },
      send: (session, text) =>
        sendMessage({ org_slug: orgSlug, thread_id: session.threadId, visitor_token: session.token, text }),
    }),
    [initSession, orgSlug, sendMessage],
  );
  const [session, setSession] = useRestoredSession(channel, token);
  const { results, status, loadMore } = useUIMessages(
    api.customerChat.public_messages.listThreadMessages,
    session === null ? "skip" : { threadId: session.threadId, visitor_token: session.token, org_slug: orgSlug },
    LIST_OPTIONS,
  );
  const rows = useMemo(() => results.filter(isFullUIMessage), [results]);
  return (
    <VisitorChatBody
      token={token}
      askText={askText}
      channel={channel}
      session={session}
      onSession={setSession}
      messages={{ rows, status, loadMore }}
    />
  );
}

function WebsiteVisitorChat({
  token,
  askText,
  siteKey,
  handshakeToken,
}: {
  readonly token: string | null;
  readonly askText: string | null;
  readonly siteKey: string;
  readonly handshakeToken: string;
}): ReactElement {
  const initSession = useMutation(api.customerChat.embed_widget.initSession);
  const sendMessage = useMutation(api.customerChat.embed_messages.sendMessage);
  // Ref: a renewed token must not restore the session again.
  const handshake = useRef(handshakeToken);
  handshake.current = handshakeToken;
  const channel = useMemo(
    (): VisitorChannelApi => ({
      grantTarget: () => ({ surface: "website_embed", siteKey, handshakeToken: handshake.current }),
      scope: visitorChatScope({ kind: "website", siteKey }),
      restore: async (visitorToken) => {
        const result = await initSession({ site_key: siteKey, handshake_token: handshake.current, visitor_token: visitorToken });
        return { threadId: result.thread_id, token: result.visitor_token };
      },
      send: (session, text) =>
        sendMessage({ site_key: siteKey, thread_id: session.threadId, visitor_token: session.token, text }),
    }),
    [initSession, sendMessage, siteKey],
  );
  const [session, setSession] = useRestoredSession(channel, token);
  const { results, status, loadMore } = useUIMessages(
    api.customerChat.embed_messages.listThreadMessages,
    session === null ? "skip" : { threadId: session.threadId, visitor_token: session.token, site_key: siteKey },
    LIST_OPTIONS,
  );
  const rows = useMemo(() => results.filter(isFullUIMessage), [results]);
  return (
    <VisitorChatBody
      token={token}
      askText={askText}
      channel={channel}
      session={session}
      onSession={setSession}
      messages={{ rows, status, loadMore }}
    />
  );
}

function VisitorChatBody({
  token,
  askText,
  channel,
  session,
  onSession,
  messages,
}: {
  readonly token: string | null;
  readonly askText: string | null;
  readonly channel: VisitorChannelApi;
  readonly session: VisitorSession | null;
  readonly onSession: (next: VisitorSession) => void;
  readonly messages: VisitorMessages;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const tBotCheck = useTranslations("messenger.botCheck");
  const errorMessage = useServerErrorMessage();
  const { config, audience } = useMessenger();
  const orgName = audience.kind === "visitor" ? audience.orgName : config.agent_name;
  const { rows, status, loadMore } = messages;
  // One list before and after the first message creates the session: nothing blinks away.
  const pending = useMessengerPendingSend(rows, session !== null && status !== "LoadingFirstPage");

  const welcome = useMemo(
    (): MessengerBubbleItem[] =>
      config.welcome_message.length > 0
        ? [{ id: "welcome", author: "agent", name: null, text: config.welcome_message, at: null }]
        : [],
    [config.welcome_message],
  );
  const items = useMemo(
    (): MessengerBubbleItem[] => [
      ...welcome,
      ...rows.map(toMessengerBubble).filter((m): m is MessengerBubbleItem => m !== null),
      ...(pending.bubble === null ? [] : [pending.bubble]),
    ],
    [pending.bubble, rows, welcome],
  );
  const awaiting = pending.bubble !== null || isAwaitingAgentAnswer(rows[rows.length - 1]);
  // Failed send or bot check: said in the thread, where the answer would be.
  const [failure, setFailure] = useState<string | null>(null);
  const lastItem = items[items.length - 1];
  const scrollRef = useMessengerStickToBottom(
    `${items.length}:${lastItem?.text.length ?? 0}:${lastItem?.tools?.length ?? 0}:${awaiting}:${failure ?? ""}`,
  );

  // Invisible bot check runs while the visitor types; the first send only collects the token.
  // A doubted browser gets Cloudflare's checkbox in `botCheckSlot`; sending waits for its click.
  const botCheckSlot = useRef<HTMLDivElement | null>(null);
  const prefetch = useRef<TurnstilePrefetch | null>(null);
  const [needsClick, setNeedsClick] = useState<boolean>(false);
  useEffect(() => {
    const started = prefetchTurnstileToken(botCheckSlot.current ?? undefined, setNeedsClick);
    prefetch.current = started;
    return () => {
      started.cancel();
      if (prefetch.current === started) prefetch.current = null;
    };
  }, []);
  const granted = useRef<boolean>(false);
  // Ref: a retry within the same send must reuse the chat the first grant opened.
  const chatToken = useRef<string | null>(token);
  const grant = async (): Promise<VisitorSession | null> => {
    const slot = botCheckSlot.current ?? undefined;
    const take = prefetch.current?.take ?? (() => fetchTurnstileToken(slot, undefined, setNeedsClick));
    const result = await requestVisitorGrant(channel.grantTarget(), chatToken.current, take);
    if (!result.ok) {
      setFailure(tBotCheck(visitorGrantFailureKey(result.code)));
      return null;
    }
    chatToken.current = result.visitorToken;
    const next: VisitorSession = { threadId: result.threadId, token: result.visitorToken };
    granted.current = true;
    onSession(next);
    return next;
  };

  const send = (text: string): Promise<boolean> =>
    pending.track(text, async () => {
      setFailure(null);
      try {
        let current = granted.current ? session : await grant();
        if (current === null) return false;
        try {
          await channel.send(current, text);
        } catch (error) {
          // Grant used up or expired: check again once, then resend.
          if (!isVisitorVerificationRequired(error)) throw error;
          current = await grant();
          if (current === null) return false;
          await channel.send(current, text);
        }
        // Start view lists it; the bubble reopens the chat written in last.
        rememberVisitorChatToken(channel.scope, current.token);
        return true;
      } catch (error) {
        setFailure(errorMessage(error, t("fehler")));
        return false;
      }
    });

  const sendRef = useRef(send);
  sendRef.current = send;
  const asked = useRef<boolean>(false);
  useEffect(() => {
    if (askText === null || asked.current) return;
    asked.current = true;
    void sendRef.current(askText);
  }, [askText]);

  return (
    <>
      <MessengerDetailHeader
        title={orgName}
        leading={<MessengerAgentAvatar size="md" />}
        actions={token !== null || session !== null ? <MessengerNewChatButton /> : null}
      />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {status === "CanLoadMore" ? <MessengerLoadOlder onClick={() => loadMore(PAGE_SIZE)} /> : null}
        <MessengerBubbles
          items={items}
          footer={
            awaiting && !needsClick ? (
              <MessengerTypingBubble searching={pending.bubble !== null || rows[rows.length - 1]?.role === "user"} />
            ) : failure !== null ? (
              <MessengerFailedRow text={failure} onHandToTeam={null} />
            ) : null
          }
        />
      </div>
      <div ref={botCheckSlot} className="flex shrink-0 justify-center px-3 empty:hidden [&>div:empty]:hidden" />
      <MessengerComposer autoFocus clearAtOnce onSend={send} disabledReason={needsClick ? tBotCheck("erstBestaetigen") : null} />
    </>
  );
}
